import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiActivity,
    FiRefreshCcw,
    FiSlash,
    FiUserCheck,
    FiUsers,
} from "react-icons/fi";

import API from "../../../../../utils/apiCalling";
import {
    config,
} from "../../../../../utils/apiUrl";

import UserFilters from "./UserFilters";
import UserTable from "./UserTable";
import UserDetailsDrawer from "./UserDetailsDrawer";
import AddEditUser from "./AddEditUser";
import ChangeStatusDialog from "./ChangeStatusDialog";

import "./UserManagement.css";

const INITIAL_FILTERS = {
    search: "",
    status: "",
};

const INITIAL_PAGINATION = {
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
};

const UsersManagement = () => {
    const api = useMemo(
        () => new API({
        tokenKey:
            "platformAccessToken"}),
        []
    );

    const [users, setUsers] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    const [filters, setFilters] =
        useState({
            ...INITIAL_FILTERS,
        });

    const [
        appliedFilters,
        setAppliedFilters,
    ] = useState({
        ...INITIAL_FILTERS,
    });

    const [
        pagination,
        setPagination,
    ] = useState({
        ...INITIAL_PAGINATION,
    });

    const [
        selectedUser,
        setSelectedUser,
    ] = useState(null);

    const [
        drawerOpen,
        setDrawerOpen,
    ] = useState(false);

    const [
        addEditOpen,
        setAddEditOpen,
    ] = useState(false);

    const [
        statusDialogOpen,
        setStatusDialogOpen,
    ] = useState(false);

    const [
        summary,
        setSummary,
    ] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        blocked: 0,
    });

    const loadUsers = useCallback(
        async () => {
            try {
                setLoading(true);
                setError("");

                const query =
                    new URLSearchParams({
                        page: String(
                            pagination.page
                        ),

                        limit: String(
                            pagination.limit
                        ),

                        search:
                            appliedFilters.search ||
                            "",

                        status:
                            appliedFilters.status ||
                            "",
                    });

                const response =
                    await api.get(
                        `${config.getUsers}?${query.toString()}`
                    );

                if (
                    response?.status !==
                    "success"
                ) {
                    throw new Error(
                        response?.message ||
                        "Unable to fetch users."
                    );
                }

                const records =
                    Array.isArray(
                        response?.data
                    )
                        ? response.data
                        : [];

                const totalRecords =
                    Number(
                        response?.totalRecords ??
                        response?.pagination
                            ?.total ??
                        0
                    );

                const totalPages =
                    Math.max(
                        Number(
                            response?.pagination
                                ?.totalPages
                        ) ||
                        Math.ceil(
                            totalRecords /
                            pagination.limit
                        ) ||
                        1,
                        1
                    );

                setUsers(records);

                setPagination(
                    (previous) => ({
                        ...previous,
                        totalRecords,
                        totalPages,
                    })
                );

                /*
                 * This summary is calculated
                 * from the current page.
                 *
                 * Later you can return a global
                 * summary from the backend.
                 */
                setSummary({
                    total: totalRecords,

                    active:
                        records.filter(
                            (user) =>
                                String(
                                    user?.status ||
                                    ""
                                ).toUpperCase() ===
                                "ACTIVE"
                        ).length,

                    inactive:
                        records.filter(
                            (user) =>
                                String(
                                    user?.status ||
                                    ""
                                ).toUpperCase() ===
                                "INACTIVE"
                        ).length,

                    blocked:
                        records.filter(
                            (user) =>
                                String(
                                    user?.status ||
                                    ""
                                ).toUpperCase() ===
                                "BLOCKED"
                        ).length,
                });
            } catch (requestError) {
                console.error(
                    "Load users error:",
                    requestError
                );

                setUsers([]);

                setPagination(
                    (previous) => ({
                        ...previous,
                        totalRecords: 0,
                        totalPages: 1,
                    })
                );

                setSummary({
                    total: 0,
                    active: 0,
                    inactive: 0,
                    blocked: 0,
                });

                setError(
                    requestError?.response
                        ?.data?.message ||
                    requestError?.message ||
                    "Unable to fetch users."
                );
            } finally {
                setLoading(false);
            }
        },
        [
            api,
            appliedFilters,
            pagination.page,
            pagination.limit,
        ]
    );

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleSearch = () => {
        setPagination(
            (previous) => ({
                ...previous,
                page: 1,
            })
        );

        setAppliedFilters({
            search:
                String(
                    filters.search || ""
                ).trim(),

            status:
                filters.status || "",
        });
    };

    const handleReset = () => {
        setFilters({
            ...INITIAL_FILTERS,
        });

        setAppliedFilters({
            ...INITIAL_FILTERS,
        });

        setPagination(
            (previous) => ({
                ...previous,
                page: 1,
            })
        );
    };

    const handleAddUser = () => {
        setSelectedUser(null);
        setAddEditOpen(true);
    };

    const handleView = (user) => {
        setSelectedUser(user);
        setDrawerOpen(true);
    };

    const handleEdit = (user) => {
        setSelectedUser(user);
        setAddEditOpen(true);
    };

    const handleStatus = (
        user
    ) => {
        setSelectedUser(user);
        setStatusDialogOpen(true);
    };

    const handleOperationSuccess =
        async () => {
            setSelectedUser(null);
            setAddEditOpen(false);
            setStatusDialogOpen(false);

            await loadUsers();
        };

    const handlePageChange = (
        nextPage
    ) => {
        if (
            nextPage < 1 ||
            nextPage >
            pagination.totalPages
        ) {
            return;
        }

        setPagination(
            (previous) => ({
                ...previous,
                page: nextPage,
            })
        );
    };

    const handleLimitChange = (
        nextLimit
    ) => {
        setPagination(
            (previous) => ({
                ...previous,
                page: 1,
                limit: Number(
                    nextLimit
                ),
            })
        );
    };

    const summaryCards = [
        {
            key: "total",
            title: "Total Customers",
            value: summary.total,
            icon: FiUsers,
            className:
                "user-summary-blue",
        },
        {
            key: "active",
            title:
                "Active on This Page",
            value: summary.active,
            icon: FiUserCheck,
            className:
                "user-summary-green",
        },
        {
            key: "inactive",
            title:
                "Inactive on This Page",
            value: summary.inactive,
            icon: FiActivity,
            className:
                "user-summary-orange",
        },
        {
            key: "blocked",
            title:
                "Blocked on This Page",
            value: summary.blocked,
            icon: FiSlash,
            className:
                "user-summary-red",
        },
    ];

    return (

            <div className="user-management">
                <div className="user-management-header">
                    <div>
                        <span>
                            WEBSITE CUSTOMER MANAGEMENT
                        </span>

                        {/* <h3>
                            Website Customers
                        </h3> */}

                        <p>
                            Manage customers who
                            register on the client
                            website and place online
                            orders.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="user-management-refresh"
                        onClick={loadUsers}
                        disabled={loading}
                        title="Refresh customers"
                    >
                        <FiRefreshCcw
                            className={
                                loading
                                    ? "spinning"
                                    : ""
                            }
                        />
                    </button>
                </div>

                <div className="user-summary-grid">
                    {summaryCards.map(
                        (card) => {
                            const Icon =
                                card.icon;

                            return (
                                <div
                                    key={card.key}
                                    className={`user-summary-card ${card.className}`}
                                >
                                    <div className="user-summary-icon">
                                        <Icon />
                                    </div>

                                    <div>
                                        <span>
                                            {card.title}
                                        </span>

                                        <strong>
                                            {loading
                                                ? "..."
                                                : card.value}
                                        </strong>
                                    </div>
                                </div>
                            );
                        }
                    )}
                </div>

                <UserFilters
                    filters={filters}
                    setFilters={setFilters}
                    handleSearch={
                        handleSearch
                    }
                    handleReset={
                        handleReset
                    }
                    handleAddUser={
                        handleAddUser
                    }
                    loading={loading}
                />

                {error ? (
                    <div className="user-management-error">
                        {error}
                    </div>
                ) : null}

                <UserTable
                    users={users}
                    loading={loading}
                    page={pagination.page}
                    limit={pagination.limit}
                    totalRecords={
                        pagination.totalRecords
                    }
                    totalPages={
                        pagination.totalPages
                    }
                    setPage={
                        handlePageChange
                    }
                    onLimitChange={
                        handleLimitChange
                    }
                    handleView={
                        handleView
                    }
                    handleEdit={
                        handleEdit
                    }
                    handleStatus={
                        handleStatus
                    }
                />

                <UserDetailsDrawer
                    open={drawerOpen}
                    user={selectedUser}
                    onClose={() => {
                        setDrawerOpen(false);
                        setSelectedUser(null);
                    }}
                    onEdit={(user) => {
                        setDrawerOpen(false);
                        handleEdit(user);
                    }}
                    onChangeStatus={(
                        user
                    ) => {
                        setDrawerOpen(false);
                        handleStatus(user);
                    }}
                />

                <AddEditUser
                    open={addEditOpen}
                    user={selectedUser}
                    onClose={() => {
                        setAddEditOpen(false);
                        setSelectedUser(null);
                    }}
                    onSuccess={
                        handleOperationSuccess
                    }
                />

                <ChangeStatusDialog
                    open={
                        statusDialogOpen
                    }
                    user={selectedUser}
                    onClose={() => {
                        setStatusDialogOpen(
                            false
                        );

                        setSelectedUser(
                            null
                        );
                    }}
                    onSuccess={
                        handleOperationSuccess
                    }
                />
            </div>
    );
};

export default UsersManagement;